import React, { Component } from 'react';

// Self-contained searchable single-select input. Drop-in child for a
// ReferenceInput: it receives the same props ReferenceInput injects
// (choices, input, meta) and works without touching RA's AutocompleteInput
// internals. Works in both SimpleForm and Filter contexts.
// Requires each choice to expose { id, name }.
class SearchableSelectInput extends Component {
  constructor(props) {
    super(props);
    this.state = { query: '', open: false };
    this._ref = React.createRef();
    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  componentDidUpdate(prevProps) {
    // When the form sets an initial value (edit mode), show its label in the input.
    const { input, choices } = this.props;
    if (input.value && choices && choices !== prevProps.choices && !this.state.query) {
      const selected = choices.find(c => c.id === input.value);
      if (selected) this.setState({ query: selected.name }); // eslint-disable-line react/no-did-update-set-state
    }
  }

  _handleOutsideClick(e) {
    if (this._ref.current && !this._ref.current.contains(e.target)) {
      this._close();
    }
  }

  _close() {
    const { input, choices } = this.props;
    if (input.value) {
      // Value is set. If the user emptied the box, treat closing as an
      // intentional clear; otherwise (a half-typed search) revert to the
      // selected item's name so an abandoned search doesn't wipe the value.
      if (this.state.query.trim() === '') {
        this._clear();
      } else {
        const selected = choices && choices.find(c => c.id === input.value);
        this.setState({ query: selected ? selected.name : '', open: false });
      }
    } else {
      this.setState({ open: false });
    }
  }

  _filtered() {
    const { choices } = this.props;
    if (!choices) return [];
    const q = this.state.query.toLowerCase();
    if (!q) return choices;
    return choices.filter(c => c.name.toLowerCase().includes(q));
  }

  _select(choice) {
    this.props.input.onChange(choice.id);
    this.setState({ query: choice.name, open: false });
  }

  _clear() {
    // Unset the value: empty string is stripped from filters by RA's
    // removeEmpty and clears the field in a form.
    this.props.input.onChange('');
    this.setState({ query: '', open: false });
  }

  render() {
    const { input, meta = {}, label, placeholder } = this.props;
    const { query, open } = this.state;
    const filtered = this._filtered();
    const hasError = meta.touched && meta.error;
    const placeholderText = placeholder || label || '';

    return (
      <div ref={this._ref} style={{ position: 'relative', marginBottom: 8 }}>
        {label && (
          <label style={{ fontSize: 12, color: hasError ? '#f44336' : 'rgba(0,0,0,0.54)', display: 'block', marginBottom: 4 }}>
            {label}
          </label>
        )}
        <input
          type="text"
          value={query}
          placeholder={placeholderText}
          autoComplete="off"
          onFocus={() => this.setState({ open: true })}
          onChange={e => this.setState({ query: e.target.value, open: true })}
          onBlur={() => {
            // Delay close so click on option registers first.
            setTimeout(() => this._close(), 150);
          }}
          style={{
            width: '100%',
            border: 'none',
            borderBottom: `1px solid ${hasError ? '#f44336' : 'rgba(0,0,0,0.42)'}`,
            padding: '6px 24px 6px 0',
            fontSize: 16,
            outline: 'none',
            background: 'transparent',
            boxSizing: 'border-box',
          }}
        />
        {input.value ? (
          <span
            role="button"
            aria-label="Limpiar selección"
            title="Limpiar"
            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); this._clear(); }}
            style={{
              position: 'absolute',
              right: 0,
              top: label ? 24 : 4,
              width: 20,
              height: 20,
              lineHeight: '18px',
              textAlign: 'center',
              fontSize: 16,
              color: 'rgba(0,0,0,0.4)',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'rgba(0,0,0,0.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(0,0,0,0.4)'; }}
          >
            ×
          </span>
        ) : null}
        {hasError && (
          <span style={{ color: '#f44336', fontSize: 12 }}>{meta.error}</span>
        )}
        {open && filtered.length > 0 && (
          <ul style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 250,
            overflowY: 'auto',
            margin: 0,
            padding: 0,
            listStyle: 'none',
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000,
            borderRadius: 2,
          }}>
            {filtered.map(c => (
              <li
                key={c.id}
                onMouseDown={() => this._select(c)}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  fontSize: 14,
                  color: c.id === input.value ? '#3f51b5' : 'rgba(0,0,0,0.87)',
                  background: c.id === input.value ? 'rgba(63,81,181,0.08)' : 'transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = c.id === input.value ? 'rgba(63,81,181,0.08)' : 'transparent'; }}
              >
                {c.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
}

export default SearchableSelectInput;
